import { useContext, useEffect } from 'react';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

import { Button, Checkbox, Skeleton } from '@mui/joy';
import { Form, Formik } from 'formik';

import { AccessKeyActionType, CrmOptionType } from '~/common/enums';
import { AppContext } from '@/shared/context/context';
import signalRMessages from '@/redux/actions/signalRMessages';
import { trpc } from '@/config/trpc';
import useMutateWithSnackbar from '@/shared/hooks/useMutateWithSnackbar';
import { type UseToggle } from '@/shared/hooks/UseToggle';

import { AccessKeyIcon, CancelIcon, SaveIcon } from '@/shared/icons/Icons';
import useAccessKeyActionFormikConfig, {
  type AccessKeyActionFormikValues,
} from '../hooks/useAccessKeyActionFormikConfig';
import AddAccessKeyActionForm from './AddAccessKeyActionForm';
import KpcModal from '@/shared/components/2.0/layout/KpcModal';

type Props = {
  toggle: UseToggle;
  selectedAccessKeyInstanceId?: string;
  defaultActionType?: AccessKeyActionType;
  actionGroupId?: string;
};

const AddAccessKeyActionFormModal: React.FC<Props> = ({
  toggle,
  selectedAccessKeyInstanceId,
  defaultActionType,
  actionGroupId,
}) => {
  const { t } = useTranslation();
  const { socket } = useContext(AppContext);
  const trpcUtil = trpc.useUtils();
  const { initialValues, validationSchema, isLoading } =
    useAccessKeyActionFormikConfig(defaultActionType);
  const { mutateAsync: createAccessKeyActionsAsync, isLoading: isSubmitting } =
    trpc.accessKeys.actions.create.useMutation();

  const createWithSnackbar = useMutateWithSnackbar({
    actionKey: 'create',
    entityKey: '_accessKeyActions.label',
    mutateAsync: createAccessKeyActionsAsync,
    onSuccess: () => {
      trpcUtil.accessKeys.actions.get.invalidate();
      trpcUtil.accessKeys.actions.getLendingsAndReservations.invalidate();
      trpcUtil.accessKeys.instances.available.invalidate();
      trpcUtil.accessKeys.get.invalidate();
      toggle.hide();
    },
  });

  useEffect(() => {
    if (!socket) {
      return undefined;
    }

    const debouncedInvalidateAvailable = debounce(() => {
      trpcUtil.accessKeys.instances.available.invalidate();
    }, 300);

    const handleAccessKeyDeleted = () => {
      debouncedInvalidateAvailable();
    };

    socket.on(signalRMessages.AccessKeyDeleted, handleAccessKeyDeleted);

    return () => {
      debouncedInvalidateAvailable.cancel();
      socket.off(signalRMessages.AccessKeyDeleted, handleAccessKeyDeleted);
    };
  }, [socket, trpcUtil.accessKeys.instances.available]);

  const handleSubmit = (values: AccessKeyActionFormikValues) => {
    const { borrower, ...rest } = values;
    const borrowerContactId = borrower?.type === CrmOptionType.Contact ? borrower.value : null;
    const borrowerCompanyId = borrower?.type === CrmOptionType.Company ? borrower.value : null;

    createWithSnackbar({
      accessKeys: rest.accessKeys.map(({ accessKeyId, accessKeyInstanceId }) => ({
        accessKeyId,
        accessKeyInstanceId,
      })),
      actionType: rest.actionType,
      borrowerCompanyId: borrowerCompanyId ?? undefined,
      borrowerContactId: borrowerContactId ?? undefined,
      caseId: rest.caseId ?? undefined,
      fromDate: rest.fromDate ?? undefined,
      remark: rest.remark?.trim() ? rest.remark.trim() : undefined,
      shouldInformBorrower: rest.shouldInformBorrower ?? undefined,
      shouldInformCoOwner: rest.shouldInformCoOwner ?? undefined,
      untilDate: rest.untilDate ?? undefined,
    });
  };

  return (
    <KpcModal
      className="w-3/4"
      title={t('_accessKeyActions.registerAction')}
      toggle={toggle}
      icon={<AccessKeyIcon />}
    >
      {isLoading ? (
        <div className="flex h-full flex-col gap-4">
          <Skeleton className="rounded-lg" variant="rectangular" height={56} />
          <div className="flex flex-row gap-4">
            <Skeleton className="rounded-lg" variant="rectangular" height={56} />
            <Skeleton className="rounded-lg" variant="rectangular" height={56} />
          </div>
          <Skeleton className="rounded-lg" variant="rectangular" height={32} />
          <Skeleton className="rounded-lg" variant="rectangular" height={56} />
          <Skeleton className="rounded-lg" variant="rectangular" height={56} />
          <div className="flex justify-end gap-4">
            <Skeleton className="rounded-lg" variant="rectangular" height={32} width={115} />
            <Skeleton className="rounded-lg" variant="rectangular" height={32} width={100} />
          </div>
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnMount
        >
          {({ isValid, setFieldValue, values }) => (
            <Form className="flex h-full flex-col gap-4">
              <AddAccessKeyActionForm
                selectedAccessKeyInstanceId={selectedAccessKeyInstanceId}
                actionGroupId={actionGroupId}
              />
              <div>
                {values.actionType !== AccessKeyActionType.Return && (
                  <div className="float-left flex flex-row gap-4">
                    <Checkbox
                      name="shouldInformCoOwner"
                      label={t('_accessKeyActions.informCoOwnerWhenLendingOrReserving')}
                      checked={values.shouldInformCoOwner}
                      onChange={(e) => setFieldValue('shouldInformCoOwner', e.target.checked)}
                    />
                    <Checkbox
                      name="shouldInformBorrower"
                      label={t('_accessKeyActions.informBorrowerWhenLendingOrReserving')}
                      checked={values.shouldInformBorrower}
                      onChange={(e) => setFieldValue('shouldInformBorrower', e.target.checked)}
                    />
                  </div>
                )}
                <div className="float-right flex flex-row gap-4">
                  <Button
                    variant="outlined"
                    color="neutral"
                    startDecorator={<CancelIcon />}
                    onClick={toggle.hide}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    type="submit"
                    startDecorator={<SaveIcon />}
                    disabled={!isValid}
                    loading={isSubmitting}
                  >
                    {t('save')}
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </KpcModal>
  );
};

export default AddAccessKeyActionFormModal;
